<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'

const STATUS_KEY_TO_FILTER: Record<string, string> = {
  draft: 'DRAFT',
  pending: 'WAITING',
  progress: 'IN_PROGRESS',
  blocked: 'BLOCKED_BUSINESS',
  validated: 'VALIDATED',
  canceled: 'CANCELED',
}

const eur = (n: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n)

interface StatusItem {
  key: string
  label: string
  amount: number
  count: number
  color: string
}

interface ActionItem {
  title: string
  value: number
  hint: string
}

interface PipelineStats {
  total: number
  openPipe: number
  validated: number
  securedRate: number
  orders: number
  regulesCount: number
  regulesSum: number
  statuses: StatusItem[]
  actions: ActionItem[]
}

const router = useRouter()
const suiteStore = useSuiteStore()
const boostCoreStore = useBoostCorePermissionStore()
const canSelectTeam = computed(() => boostCoreStore.can('SALES_MANAGER'))
const scope = ref<'me' | 'team'>('me')
const period = ref<'month' | 'year'>('month')
const data = ref<PipelineStats | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

async function fetchPipelineStats() {
  loading.value = true
  error.value = null
  const effectiveScope = canSelectTeam.value ? scope.value : 'me'
  const res = await suiteStore.suiteFetch(
    `orders/pipeline-stats?scope=${effectiveScope}&period=${period.value}`,
    { method: 'GET', credentials: 'include' },
  )
  loading.value = false
  if (!res || !res.ok) {
    error.value = 'Impossible de charger les stats'
    return
  }
  data.value = await res.json()
}

onMounted(() => fetchPipelineStats())
watch([scope, period, canSelectTeam], () => fetchPipelineStats())

const validated = computed(() => data.value?.validated ?? 0)
const regulesCount = computed(() => data.value?.regulesCount ?? 0)
const openPipe = computed(() => data.value?.openPipe ?? 0)
const securedRate = computed(() => data.value?.securedRate ?? 0)

const periodLabel = computed(() => {
  const now = new Date()
  return period.value === 'month'
    ? now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    : String(now.getFullYear())
})

function goToDashboardWithStatus(statusKey: string) {
  const filterStatus = STATUS_KEY_TO_FILTER[statusKey]
  if (filterStatus) {
    router.push({ path: '/dashboard/orders', query: { status: filterStatus } })
  }
}
</script>

<template>
  <div class="space-y-4">
    <div
      class="flex flex-col gap-3 rounded-xl border border-base-300 bg-base-100 p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:gap-4"
    >
      <div class="flex shrink-0 items-center">
        <h1 class="text-xl font-semibold tracking-tight">Bons de commande et Régules</h1>
      </div>

      <div class="flex shrink-0 flex-wrap items-center gap-2">
        <div v-if="canSelectTeam" class="join">
          <button
            type="button"
            class="btn btn-sm join-item"
            :class="scope === 'me' ? 'btn-active' : ''"
            @click="scope = 'me'"
          >
            Moi
          </button>
          <button
            type="button"
            class="btn btn-sm join-item"
            :class="scope === 'team' ? 'btn-active' : ''"
            @click="scope = 'team'"
          >
            Équipe
          </button>
        </div>

        <div class="join">
          <button
            type="button"
            class="btn btn-sm join-item"
            :class="period === 'month' ? 'btn-active' : ''"
            @click="period = 'month'"
          >
            Mois
          </button>
          <button
            type="button"
            class="btn btn-sm join-item"
            :class="period === 'year' ? 'btn-active' : ''"
            @click="period = 'year'"
          >
            Année
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center py-8">
      <span class="loading loading-spinner loading-lg" />
    </div>
    <div v-else-if="error" class="rounded-xl border border-error bg-error/10 p-4 text-error">
      {{ error }}
    </div>
    <div v-else-if="data" class="grid gap-4 xl:grid-cols-[1.45fr_0.95fr] xl:items-stretch">
      <div class="flex min-h-0 w-full">
        <div class="flex w-full flex-col rounded-xl border border-base-300 bg-base-100 p-4 shadow-sm">
          <div class="mb-3 flex items-center justify-between gap-2">
            <div>
              <div class="text-base font-semibold">Répartition Marge Compteur {{ periodLabel }}</div>
            </div>
          </div>
          <div class="space-y-3">
            <div class="flex h-4 w-full overflow-hidden rounded-full bg-base-300">
              <div
                v-for="status in data.statuses"
                :key="status.key"
                :title="`${status.label} — ${eur(status.amount)}`"
                class="transition-all"
                :style="{
                  width: `${data.total > 0 ? (status.amount / data.total) * 100 : 0}%`,
                  backgroundColor: status.color,
                }"
              />
            </div>
            <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <button
                v-for="status in data.statuses"
                :key="status.key"
                type="button"
                class="rounded-lg border border-base-300 bg-base-100 p-2 text-left transition hover:border-primary hover:bg-base-200"
                @click="goToDashboardWithStatus(status.key)"
              >
                <div class="mb-1 flex items-center gap-2">
                  <span
                    class="h-2.5 w-2.5 rounded-full"
                    :style="{ backgroundColor: status.color }"
                  />
                  <span class="text-xs text-base-content/70">{{ status.label }}</span>
                </div>
                <div class="text-sm font-semibold">{{ eur(status.amount) }}</div>
                <div class="text-xs text-base-content/70">{{ status.count }} éléments</div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="flex min-h-0 w-full">
        <div class="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 sm:grid-rows-[1fr_1fr]">
          <div class="rounded-xl bg-accent p-4 text-accent-content">
            <div class="text-xs opacity-80">Marge Compteur atteignable</div>
            <div class="mt-2 text-2xl font-semibold">{{ eur(data.total) }}</div>
            <div class="mt-1 text-xs opacity-80">Inclut validé + ouvert</div>
          </div>
          <div class="rounded-xl border border-base-300 bg-base-100 p-4">
            <div class="text-xs text-base-content/70">Ouvert à convertir</div>
            <div class="mt-2 text-2xl font-semibold">{{ eur(openPipe) }}</div>
            <div class="mt-1 text-xs text-base-content/70">hors validés / annulés</div>
          </div>
          <div class="rounded-xl border border-base-300 bg-base-100 p-4">
            <div class="text-xs text-base-content/70">Déjà sécurisé</div>
            <div class="mt-2 text-2xl font-semibold">{{ securedRate }}%</div>
            <div class="mt-1 text-xs text-base-content/70">{{ eur(validated) }} validés</div>
          </div>
          <div class="rounded-xl border border-base-300 bg-base-100 p-4">
            <div class="text-xs text-base-content/70">{{ regulesCount }} régules</div>
            <div class="mt-2 text-2xl font-semibold">{{ eur(data.regulesSum) }}</div>
            <div class="mt-1 text-xs text-base-content/70">montant total</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
